import { useState, useRef, useCallback } from "react";
import { api } from "@/lib/api";
import { Upload, X, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export const ImageDropzone = ({ value = [], onChange, max = 10 }) => {
  const [uploading, setUploading] = useState(false);
  const [drag, setDrag] = useState(false);
  const inputRef = useRef(null);

  const handleFiles = useCallback(async (files) => {
    const arr = Array.from(files);
    if (!arr.length) return;
    if (value.length + arr.length > max) {
      toast.error(`You can upload up to ${max} images`);
      return;
    }
    setUploading(true);
    const uploaded = [];
    for (const file of arr) {
      if (!file.type.startsWith("image/")) continue;
      if (file.size > 8 * 1024 * 1024) {
        toast.error(`${file.name} exceeds 8MB`);
        continue;
      }
      const form = new FormData();
      form.append("file", file);
      try {
        const { data } = await api.post("/uploads/image", form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        uploaded.push(`${BACKEND}${data.url}`);
      } catch {
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    setUploading(false);
    if (uploaded.length) onChange([...value, ...uploaded]);
  }, [value, onChange, max]);

  const onDrop = (e) => {
    e.preventDefault();
    setDrag(false);
    handleFiles(e.dataTransfer.files);
  };

  const removeAt = (i) => onChange(value.filter((_, idx) => idx !== i));

  return (
    <div data-testid="image-dropzone">
      <div
        onDragEnter={(e) => { e.preventDefault(); setDrag(true); }}
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        className={`cursor-pointer rounded-2xl border-2 border-dashed p-8 flex flex-col items-center justify-center text-center transition-colors ${drag ? "border-[var(--primary)] bg-[rgba(200,90,50,0.05)]" : "border-[var(--line-strong)]"}`}
        data-testid="dropzone-area"
      >
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={(e) => handleFiles(e.target.files)}
          data-testid="dropzone-input"
        />
        <Upload className="w-8 h-8 text-[var(--primary)]" />
        <div className="font-serif text-lg text-[var(--teak)] mt-3">
          {uploading ? "Uploading…" : "Drop property photos here"}
        </div>
        <div className="text-xs text-[var(--ink-muted)] mt-1">
          or click to browse · JPG, PNG, WebP up to 8MB · {value.length}/{max}
        </div>
      </div>

      {value.length > 0 && (
        <div className="mt-4 grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 gap-3" data-testid="dropzone-previews">
          {value.map((src, i) => (
            <div key={i} className="relative aspect-square rounded-xl overflow-hidden border border-[var(--line)] bg-[var(--surface)]">
              <img src={src} alt="" className="w-full h-full object-cover" />
              <button
                type="button"
                onClick={() => removeAt(i)}
                className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/70 text-white flex items-center justify-center hover:bg-black"
                data-testid={`remove-image-${i}`}
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      {value.length === 0 && !uploading && (
        <div className="mt-3 text-xs text-[var(--ink-muted)] flex items-center gap-1">
          <ImageIcon className="w-3.5 h-3.5" /> At least 1 real photo required — our team manually verifies before publishing.
        </div>
      )}
    </div>
  );
};
